/**
 * Lightweight code-language detection for unlabeled fences. Heuristic
 * scoring over signature patterns — no 100KB highlight.js payload. Returns
 * a lang-markdown-resolvable name, or null when unsure (better silent than
 * wrong).
 */

interface Sig {
  lang: string;
  patterns: [RegExp, number][];
}

const SIGS: Sig[] = [
  {
    lang: "json",
    patterns: [
      [/^\s*[{[]/, 2],
      [/"[\w-]+"\s*:/, 3],
      [/^[\s{}[\],:"\w.\-+eE]*$/s, 1],
    ],
  },
  {
    lang: "html",
    patterns: [
      [/<!doctype html>/i, 6],
      [/<\/(div|span|p|body|html|head|a|ul|li)>/i, 4],
      [/<(div|span|img|input|meta|link)\b[^>]*>/i, 3],
    ],
  },
  {
    lang: "css",
    patterns: [
      [/[.#][\w-]+\s*\{/, 4],
      [/[\w-]+\s*:\s*[^;{]+;/, 2],
      [/@(media|import|keyframes|font-face)/, 4],
    ],
  },
  {
    lang: "typescript",
    patterns: [
      [/\b(interface|type)\s+\w+\s*[={]/, 5],
      [/:\s*(string|number|boolean|void|unknown|any)\b/, 4],
      [/\bas\s+(const|\w+)/, 2],
      [/\b(import|export)\b.*\bfrom\s+["']/, 2],
    ],
  },
  {
    lang: "javascript",
    patterns: [
      [/\b(const|let|var)\s+\w+\s*=/, 3],
      [/=>\s*[{(]?/, 2],
      [/\bfunction\s*\w*\s*\(/, 2],
      [/console\.(log|error|warn)/, 3],
      [/\b(import|export)\b.*\bfrom\s+["']/, 2],
      [/\brequire\s*\(/, 2],
    ],
  },
  {
    lang: "python",
    patterns: [
      [/\bdef\s+\w+\s*\(.*\)\s*:/, 5],
      [/\b(import\s+\w+|from\s+[\w.]+\s+import)/, 3],
      [/\bself\b/, 2],
      [/\bprint\s*\(/, 2],
      [/\b(elif|None|True|False)\b/, 2],
    ],
  },
  {
    lang: "rust",
    patterns: [
      [/\bfn\s+\w+\s*\(/, 4],
      [/\blet\s+mut\b/, 4],
      [/::\w+/, 2],
      [/\b(impl|pub|crate|match)\b/, 2],
      [/println!\s*\(/, 4],
    ],
  },
  {
    lang: "go",
    patterns: [
      [/\bfunc\s+\w+\s*\(/, 4],
      [/\bpackage\s+\w+/, 4],
      [/:=/, 3],
      [/\bfmt\.\w+/, 3],
    ],
  },
  {
    lang: "sql",
    patterns: [
      [/\b(SELECT|INSERT INTO|UPDATE|DELETE FROM|CREATE TABLE|ALTER TABLE)\b/i, 5],
      [/\b(WHERE|GROUP BY|ORDER BY|JOIN|LIMIT)\b/i, 2],
    ],
  },
  {
    lang: "bash",
    patterns: [
      [/^#!\/bin\/(ba)?sh/, 6],
      [/^\s*(sudo|apt|npm|pnpm|yarn|git|curl|wget|cd|mkdir|echo|export)\s/m, 3],
      [/\$\{?\w+\}?/, 1],
      [/\s(&&|\|\|)\s/, 1],
      [/^\s*if\s+\[\[?\s/m, 3],
    ],
  },
  {
    lang: "yaml",
    patterns: [
      [/^\s*[\w-]+:\s*(\S|$)/m, 2],
      [/^\s*-\s+[\w-]+:\s/m, 3],
      [/^---\s*$/m, 2],
    ],
  },
  {
    lang: "java",
    patterns: [
      [/\bpublic\s+(class|static|void)\b/, 5],
      [/System\.out\.println/, 4],
      [/\bprivate\s+\w+\s+\w+;/, 3],
    ],
  },
  {
    lang: "csharp",
    patterns: [
      [/\busing\s+System/, 5],
      [/\bnamespace\s+\w+/, 3],
      [/Console\.WriteLine/, 4],
    ],
  },
];

const THRESHOLD = 5;

export function detectLanguage(code: string): string | null {
  const sample = code.slice(0, 4000);
  if (sample.trim().length < 16) return null;
  let best: { lang: string; score: number } | null = null;
  for (const sig of SIGS) {
    let score = 0;
    for (const [re, weight] of sig.patterns) {
      if (re.test(sample)) score += weight;
    }
    if (!best || score > best.score) best = { lang: sig.lang, score };
  }
  // typescript signatures imply javascript ones; prefer ts only when its own markers hit
  if (best && best.score >= THRESHOLD) return best.lang;
  return null;
}
